import { useIsHotkeyScopeEnabled } from "components/scrapEyeLayout/hotkeys/useIsHotkeyScopeEnabled";
import { useEffect, useRef, useState } from "react";
import { useAltToggleMode } from "./useAltToggleMode";

type UseAreaHotkeyModeResult = Readonly<{
  isAreaModeActive: boolean;
}>;

export const useAreaHotkeyMode = (
  ownerScope: string
): UseAreaHotkeyModeResult => {
  const { altToggleMode } = useAltToggleMode();
  const { isScopeEnabled } = useIsHotkeyScopeEnabled();
  const [isAreaModeActive, setIsAreaModeActive] = useState(false);

  const altToggleModeRef = useRef(altToggleMode);
  useEffect(() => {
    altToggleModeRef.current = altToggleMode;
  }, [altToggleMode]);

  // Area mode belongs to the labelling surface that owns the keyboard. When that
  // surface's scope is suspended in the nearest provider — as it is for the main
  // page behind an open bring-in modal — Alt must not arm area mode here, so the
  // keystrokes reach only the surface that is currently active.
  const isOwnerScopeEnabledRef = useRef(true);
  useEffect(() => {
    const enabled = isScopeEnabled(ownerScope);
    // When this surface regains ownership — e.g. the modal that suspended the
    // page closes — clear any area mode that was frozen while suspended, so the
    // page's scopes and its area indicator return to a consistent resting state.
    if (enabled && !isOwnerScopeEnabledRef.current) {
      setIsAreaModeActive(false);
    }
    isOwnerScopeEnabledRef.current = enabled;
  }, [isScopeEnabled, ownerScope]);

  useEffect(() => {
    const isAltKey = (key: string) =>
      key === "Alt" || key === "AltLeft" || key === "AltRight";

    // Track if alt was just pressed (for toggle mode debouncing)
    let altKeyWasPressed = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isAltKey(event.key) && !isAltKey(event.code)) {
        return;
      }

      if (!isOwnerScopeEnabledRef.current) {
        return;
      }

      if (altToggleModeRef.current) {
        // Toggle mode: press to toggle on/off
        if (!altKeyWasPressed) {
          altKeyWasPressed = true;
          setIsAreaModeActive((prev) => !prev);
        }
      } else {
        // Hold mode (default): set to true while held
        setIsAreaModeActive(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isAltKey(event.key) || isAltKey(event.code)) {
        altKeyWasPressed = false;
        // In hold mode, release when key is released — but only while this surface
        // owns the keyboard, so a suspended surface keeps area mode frozen rather
        // than clearing it and re-enabling the scopes area mode had disabled.
        if (!altToggleModeRef.current && isOwnerScopeEnabledRef.current) {
          setIsAreaModeActive(false);
        }
      }
    };

    const handleBlur = () => {
      if (isOwnerScopeEnabledRef.current) {
        setIsAreaModeActive(false);
      }
      altKeyWasPressed = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  return { isAreaModeActive };
};
